import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import {
  AddIcon,
  AsyncSelect,
  Button,
  FlexContent,
  Form,
  FormField,
  ModalDialog,
  Numeric,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminLockLockstopMapping } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

interface FormBody {
  lockId: { value: string; label: string } | null;
  lockstopItemId: { value: string; label: string } | null;
  quantity: number | null;
  installationLevels: number | null;
}

const AdminLockLockstopMappingsTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "lockLockstop" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useLockLockstopMappings(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminLockLockstopMapping>();
  const [itemToEdit, setItemToEdit] = useState<AdminLockLockstopMapping>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminLockLockstopMapping>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.lock}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.lockstop}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.quantity}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.installationLevels}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.lock.code}</TableCell>
              <TableCell>{item.lockstop.code}</TableCell>
              <TableCell>{item.quantity}</TableCell>
              <TableCell>{item.installationLevels}</TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteLockLockstopMapping(itemToDelete.lock.id, itemToDelete.lockstop.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
    </>
  );
};

const FormModal: FC<{ item?: AdminLockLockstopMapping; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();
  const [defaultLockOptions, setDefaultLockOptions] = useState<{ value: string; label: string }[]>([]);
  const [defaultLockstopOptions, setDefaultLockstopOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    adminApi
      .getLocks({ q: "", page: 1, limit: 100 }, new AbortController().signal)
      .then((items) => setDefaultLockOptions(items.map((x) => ({ value: x.item.id, label: x.item.code }))))
      .catch(() => setDefaultLockOptions([]));
    adminApi
      .getLockStops({ q: "", page: 1, limit: 100 }, new AbortController().signal)
      .then((items) => setDefaultLockstopOptions(items.map((x) => ({ value: x.item.id, label: x.item.code }))))
      .catch(() => setDefaultLockstopOptions([]));
  }, []);

  const defaultValues: FormBody = {
    lockId: item ? { value: item.lock.id, label: item.lock.code } : null,
    lockstopItemId: item ? { value: item.lockstop.id, label: item.lockstop.code } : null,
    quantity: item?.quantity ?? null,
    installationLevels: item?.installationLevels ?? null,
  };

  const onSubmit = () => async (formData: FormBody) => {
    if (
      !formData.lockId?.value ||
      !formData.lockstopItemId?.value ||
      formData.quantity == null ||
      formData.installationLevels == null
    )
      return;
    try {
      if (item) {
        await adminApi.updateLockLockstopMapping(item.lock.id, item.lockstop.id, {
          quantity: formData.quantity,
          installationLevels: formData.installationLevels,
        });
      } else {
        await adminApi.insertLockLockstopMapping({
          lockItemId: formData.lockId.value,
          lockstopItemId: formData.lockstopItemId.value,
          quantity: formData.quantity,
          installationLevels: formData.installationLevels,
        });
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        item
          ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.singular}`
      }
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="lockId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.lock}
                  error={fieldState.error}
                 
                >
                  {({ labelId }) => (
                    <AsyncSelect<{ value: string; label: string }, false>
                      inputId={labelId}
                      cacheOptions
                      defaultOptions
                      loadOptions={(inputValue) =>
                        adminApi
                          .getLocks({ q: inputValue || "", page: 1, limit: 20 }, new AbortController().signal)
                          .then((items) => items.map((item) => ({ value: item.item.id, label: item.item.code })))
                      }
                      isMulti={false}
                      isDisabled={!!item}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                      value={defaultLockOptions.find((o) => o.value === field.value?.value) ?? field.value}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="lockstopItemId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.lockstop}
                  error={fieldState.error}
                 
                >
                  {({ labelId }) => (
                    <AsyncSelect<{ value: string; label: string }, false>
                      inputId={labelId}
                      cacheOptions
                      defaultOptions
                      loadOptions={(inputValue) =>
                        adminApi
                          .getLockStops({ q: inputValue || "", page: 1, limit: 20 }, new AbortController().signal)
                          .then((items) => items.map((item) => ({ value: item.item.id, label: `${item.item.code}` })))
                      }
                      isMulti={false}
                      isDisabled={!!item}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                      value={defaultLockstopOptions.find((o) => o.value === field.value?.value) ?? field.value}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="quantity"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.quantity}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="installationLevels"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockLockstopMappings.installationLevels}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminLockLockstopMappingsTab;
